/**
 * Home dashboard data hook.
 *
 * Wraps GET /api/platform/dashboard. The endpoint returns `has_data=false`
 * for orgs with no cohort members yet — the caller can render the demo
 * fixtures in that case so a fresh org doesn't see a wall of zeros.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface DashboardKpis {
  members: number;
  members_delta: number;
  weekly_active_pct: number;
  weekly_active_delta_pp: number;
  msgs_per_user_per_week: number;
  msgs_delta: number;
  cost_per_user_per_week: number;
  cost_delta: number;
}

export interface DashboardAgentPulse {
  in_flight: number;
  last_hour: number;
  today_counts: {
    artifacts: number;
    agendas_opened: number;
    outreach: number;
    agendas_dropped: number;
  };
}

export interface DashboardTopTopic {
  name: string;
  count: number;
}

export interface DashboardEngagementPoint {
  day: number;
  active: number;
}

export interface DashboardResponse {
  status: "ok";
  as_of: string;
  members: number;
  has_data: boolean;
  kpis: DashboardKpis;
  agent_pulse: DashboardAgentPulse;
  top_topics: DashboardTopTopic[];
  engagement_30d: DashboardEngagementPoint[];
}

export function useDashboard() {
  return useQuery<DashboardResponse>({
    queryKey: ["platform", "dashboard"],
    queryFn: () => api.get<DashboardResponse>("/platform/dashboard"),
    // 60s stale time — dashboard counters don't change that fast,
    // and the live indicator handles real-time pulse separately.
    staleTime: 60_000,
    retry: 1,
  });
}
